/**
 * Shared Styled Components
 *
 * Purpose: Centralized location for reusable styled components used across multiple pages
 *          Eliminates duplication and ensures consistent styling
 *
 * Architecture: MUI styled components with theme integration
 * Dependencies: Material-UI styled, components
 * Author: Development Team
 * Created: 2025-01-27
 */

import { styled } from '@mui/material/styles';
import { Chip, Alert, Box, Card } from '@mui/material';

/**
 * TenantChip - Styled chip for tenant display
 *
 * Purpose: Displays tenant information with visual distinction for GLOBAL vs tenant-specific
 * Variants: .global, .tenant, .readonly
 *
 * Used in:
 * - TenantContextSwitcher.tsx
 * - TenantSelector.tsx
 */
export const TenantChip = styled(Chip)(({ theme }) => ({
  '&.global': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '&.tenant': {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  '&.readonly': {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.getContrastText(theme.palette.grey[400]),
  },
}));

/**
 * GlobalWarningBanner - Alert banner for GLOBAL context warnings
 *
 * Purpose: Displays prominent warning banners for GLOBAL tenant context
 * Styling: Bold message text with spacing
 *
 * Used in:
 * - MetadataManagerPage.tsx
 * - MDMManagerPage.tsx
 */
export const GlobalWarningBanner = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiAlert-message': {
    fontWeight: 500,
  },
}));

/**
 * ContentHeader - Header container for page sections
 *
 * Purpose: Consistent header styling for admin pages and sections
 * Styling: Card-like appearance with shadow and spacing
 *
 * Used in:
 * - MetadataManagerPage.tsx
 * - AdminPage.tsx
 */
export const ContentHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[1],
}));

/**
 * ComingSoonCard - Placeholder card for future features
 *
 * Purpose: Visual placeholder for features under development
 * Styling: Dashed border, reduced opacity, hover effect
 *
 * Used in:
 * - AdminPage.tsx
 * - PlanningPage.tsx
 */
export const ComingSoonCard = styled(Card)(({ theme }) => ({
  height: '200px',
  transition: 'all 0.3s ease',
  border: `2px dashed ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
  opacity: 0.7,
  '&:hover': {
    opacity: 1,
    backgroundColor: theme.palette.grey[100],
  },
}));
